import type { PublicPlanCard } from "@/api/queries/publicPlans";
import { minorUnitExponent } from "@/lib/money";

/**
 * What a tier actually costs a shop at a given monthly order volume.
 *
 * The pricing page prints two numbers per tier — a subscription and a
 * take-rate — and leaves the addition to the reader. That is the whole
 * reason a shop cannot tell Pro from Business by looking: the cheaper
 * subscription is not the cheaper plan once the commission is in it.
 *
 * Everything here is derived from ``GET /public/plans``. Nothing is
 * hardcoded, so an admin changing a rate or a ceiling changes the
 * recommendation without a deploy — which is the only way this can stay
 * true (the same rule the compare table already lives by).
 */

/** Minor units (haléře / cents). All arithmetic stays integer. */
export type Minor = number;

export interface PlanCost {
  tier: string;
  /** Subscription for one month, minor units. */
  subscriptionMinor: Minor;
  /** Commission for the month AFTER the ceiling is applied. */
  feeMinor: Minor;
  totalMinor: Minor;
  /** True when the month's volume pushed the commission onto its cap. */
  feeCapped: boolean;
}

/**
 * ``null`` when the tier cannot be priced from the payload, and the two
 * reasons are different in kind:
 *
 * - **No published price** (Enterprise, "cena na vyžádání"). Inventing a
 *   number for it would be the worst thing this component could do.
 * - **A ceiling denominated in another currency than the subscription.**
 *   ``platform_fee_cap_currency`` is the platform base currency and
 *   deliberately not ``plan.currency``; adding two currencies would
 *   produce a confident, wrong total. Without an FX rate on this
 *   payload the honest answer is "cannot say".
 */
export function planMonthlyCost(
  plan: PublicPlanCard,
  volumeMinor: Minor,
  billing: "monthly" | "annual",
): PlanCost | null {
  const subscription =
    billing === "annual"
      ? plan.price_annual_cents == null
        ? null
        : Math.round(plan.price_annual_cents / 12)
      : plan.price_monthly_cents;
  if (subscription == null) return null;

  const cap = plan.platform_fee_cap_monthly_minor;
  if (cap != null && plan.platform_fee_cap_currency !== plan.currency) {
    return null;
  }

  const uncapped = Math.round((Math.max(0, volumeMinor) * plan.platform_fee_bps) / 10_000);
  const feeMinor = cap == null ? uncapped : Math.min(uncapped, cap);
  return {
    tier: plan.tier,
    subscriptionMinor: subscription,
    feeMinor,
    totalMinor: subscription + feeMinor,
    feeCapped: cap != null && uncapped > cap,
  };
}

/** The volume slider: how far it reaches, how it moves, where it starts. */
export interface VolumeScale {
  max: Minor;
  step: Minor;
  start: Minor;
}

/**
 * Nearest 1–2–5 number of its decade, measured on a log scale:
 * 1 166 667 → 1 000 000, 66 600 → 50 000, 140 000 → 100 000.
 */
function niceRound(major: number): number {
  if (major <= 0) return 0;
  const decade = 10 ** Math.floor(Math.log10(major));
  const mantissa = major / decade;
  const nice = mantissa < Math.SQRT2 ? 1 : mantissa < Math.sqrt(10) ? 2 : mantissa < Math.sqrt(50) ? 5 : 10;
  return nice * decade;
}

/**
 * The slider, derived from the plans instead of written down.
 *
 * A literal range would be a money hardcode in the frontend and would go
 * stale the moment an admin repriced anything. The interesting part of the
 * scale is where the commissions stop growing, so:
 *
 * - **max** — the largest volume at which some tier's ceiling binds,
 *   rounded to a number a reader recognises (KAN-1947: the raw value ended
 *   the scale on 2 333 334 Kč);
 * - **step** — a hundredth of that, rounded the same way, so every stop is
 *   a round sum;
 * - **start** — half the volume at which the FIRST ceiling binds: a small
 *   shop whose commission is still growing, not a mid-sized one.
 *
 * With no ceilings anywhere the subscriptions are the only money on the
 * payload, and the scale spans a hundred months of the dearest one.
 * Rounding happens in major units — CZK has two decimals, JPY none.
 *
 * ``max`` is ``0`` when the payload prices nothing; the caller renders no
 * slider.
 */
export function volumeScale(plans: readonly PublicPlanCard[]): VolumeScale {
  const bindPoints: number[] = [];
  let dearest = 0;
  plans.forEach((plan) => {
    dearest = Math.max(dearest, plan.price_monthly_cents ?? 0);
    const cap = plan.platform_fee_cap_monthly_minor;
    if (cap == null || plan.platform_fee_bps <= 0) return;
    if (plan.platform_fee_cap_currency !== plan.currency) return;
    bindPoints.push((cap * 10_000) / plan.platform_fee_bps);
  });
  const unit = 10 ** minorUnitExponent(plans[0]?.currency);
  const toNice = (minor: number): Minor => Math.round(niceRound(minor / unit) * unit);

  const max = bindPoints.length ? toNice(Math.max(...bindPoints)) : toNice(dearest * 100);
  if (max <= 0) return { max: 0, step: 1, start: 0 };
  const step = Math.max(1, toNice(max / 100));
  const rawStart = bindPoints.length ? toNice(Math.min(...bindPoints) / 2) : max / 10;
  const start = Math.min(max, Math.max(step, Math.round(rawStart / step) * step));
  return { max, step, start };
}

/**
 * Tiers that can never be the cheapest, at any volume.
 *
 * A tier is out of the price race when another one costs less at its
 * worst (subscription + ceiling) than this one costs at its best (the bare
 * subscription). The calculator then never recommends it, and says so
 * instead of leaving the reader to wonder why (KAN-1947). An uncapped
 * commission has no worst case, so such a tier never rules another out.
 */
export function priceDominatedTiers(
  plans: readonly PublicPlanCard[],
  billing: "monthly" | "annual",
): Set<string> {
  const bounds = plans
    .map((plan) => {
      const atZero = planMonthlyCost(plan, 0, billing);
      if (atZero === null) return null;
      const cap = plan.platform_fee_cap_monthly_minor;
      const worst =
        plan.platform_fee_bps <= 0
          ? atZero.subscriptionMinor
          : cap == null
            ? Number.POSITIVE_INFINITY
            : atZero.subscriptionMinor + cap;
      return { tier: plan.tier, best: atZero.subscriptionMinor, worst };
    })
    .filter((b): b is { tier: string; best: number; worst: number } => b !== null);
  const out = new Set<string>();
  for (const b of bounds) {
    if (bounds.some((other) => other.tier !== b.tier && other.worst < b.best)) out.add(b.tier);
  }
  return out;
}

export interface Recommendation {
  /** Cheapest first. Empty when no tier could be priced. */
  ranked: PlanCost[];
  /** Tiers with no published price — offered as "talk to us", never as a number. */
  quotedTiers: string[];
}

/**
 * Rank the priceable tiers by what the shop would actually pay.
 *
 * Ties keep the payload's own order (``sort_order`` from the server), so
 * two tiers that cost the same at this volume recommend the lower one —
 * the one with fewer limits to run into, and the one the admin already
 * decided comes first.
 */
export function recommendPlans(
  plans: readonly PublicPlanCard[],
  volumeMinor: Minor,
  billing: "monthly" | "annual",
): Recommendation {
  const ranked: PlanCost[] = [];
  const quotedTiers: string[] = [];
  plans.forEach((plan) => {
    const cost = planMonthlyCost(plan, volumeMinor, billing);
    if (cost === null) {
      quotedTiers.push(plan.tier);
      return;
    }
    ranked.push(cost);
  });
  // Stable sort: Array.prototype.sort is stable in every engine we ship
  // to, so equal totals keep the server's order.
  ranked.sort((a, b) => a.totalMinor - b.totalMinor);
  return { ranked, quotedTiers };
}
